import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, EmbeddedActionBar } from "../components/ui-bits";
import { useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { Package, AlertTriangle, Plus, ArrowUpCircle, ArrowDownCircle, History } from "lucide-react";
import { ReceiveGoodsButton } from "../components/ReceiveGoods";

const REASONS = [
  { key: "purchase", label: "Purchase (stock in)", positive: true },
  { key: "initial", label: "Initial stock", positive: true },
  { key: "return", label: "Return from client", positive: true },
  { key: "sale", label: "Sale (stock out)", positive: false },
  { key: "production_used", label: "Used in production", positive: false },
  { key: "adjustment", label: "Manual adjustment" },
];

const empty = {
  material_id: "", quantity_change: 1, reason: "purchase",
  reference_id: "", reference_type: "", notes: "", unit_cost: 0,
};

export default function InventoryPage({ embedded = false } = {}) {
  const { canEdit } = useBrandPermissions();
  const t = useT();
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [matFilter, setMatFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    const [s, m, ms] = await Promise.all([
      api.get("/inventory/summary").then(r=>r.data).catch(()=>null),
      api.get("/inventory/movements").then(r=>r.data).catch(()=>[]),
      api.get("/materials").then(r=>r.data).catch(()=>[]),
    ]);
    setSummary(s); setMovements(m); setMaterials(ms);
  };
  useEffect(() => { loadAll(); }, []);

  const matMap = useMemo(() => Object.fromEntries(materials.map(m => [m.id, m])), [materials]);
  const filteredMovements = matFilter ? movements.filter(m => m.material_id === matFilter) : movements;

  const openNew = (materialId) => {
    setForm({ ...empty, material_id: materialId || (materials[0]?.id || "") });
    setErr(""); setModal(true);
  };

  const save = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const reasonMeta = REASONS.find(r => r.key === form.reason);
      let qty = Math.abs(Number(form.quantity_change) || 0);
      if (reasonMeta && reasonMeta.positive === false) qty = -qty;
      const payload = {
        material_id: form.material_id,
        quantity_change: qty,
        reason: form.reason,
        reference_id: form.reference_id || undefined,
        reference_type: form.reference_type || undefined,
        notes: form.notes || undefined,
        unit_cost: Number(form.unit_cost) || undefined,
      };
      await api.post("/inventory/movements", payload);
      setModal(false);
      loadAll();
    } catch (er) { setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="inventory-page">
      {!embedded && (
      <PageHeader
        eyebrow="Warehouse"
        title="Inventory"
        description="Materials on hand, running stock, and full movement ledger."
        action={canEdit && (
          <button onClick={()=>openNew()} className="btn-gold inline-flex items-center gap-2" data-testid="inv-new-movement">
            <Plus size={14}/> Record Movement
          </button>
        )}
      />
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <div className="flex gap-3">
            <ReceiveGoodsButton onDone={loadAll} className="btn-ghost inline-flex items-center gap-2"/>
            <button onClick={()=>openNew()} className="btn-gold inline-flex items-center gap-2" data-testid="inv-new-movement">
              <Plus size={14}/> {t("inventory.new_movement")}
            </button>
          </div>
        }/>
      )}

      <div className="flex gap-1 border-b border-[#E5E5E5] mb-6 overflow-x-auto">
        {[{k:"summary",l:"Summary"},{k:"ledger",l:"Ledger"}].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)}
            className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 transition-colors ${tab === t.k ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
            data-testid={`inv-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "summary" && summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="card-dark p-6">
              <div className="label-caps mb-4 flex items-center gap-2"><Package size={12}/> Materials tracked</div>
              <div className="font-serif text-4xl">{summary.materials_count}</div>
            </div>
            <div className="card-dark p-6">
              <div className="label-caps mb-4">Total inventory value</div>
              <div className="font-serif text-4xl">€ {summary.total_value.toFixed(2)}</div>
            </div>
            <div className="card-dark p-6">
              <div className="label-caps mb-4">Recent movements (30 last)</div>
              <div className="font-serif text-4xl">{summary.recent_movements.length}</div>
            </div>
          </div>

          {summary.low_stock.length > 0 && (
            <div className="card-dark p-6 mb-8 border-l-2 border-l-[#0A0A0A]" data-testid="inv-low-stock">
              <div className="flex items-center gap-2 mb-4"><AlertTriangle size={16} className="text-red-600"/><div className="label-caps text-red-700">Sotto scorta minima</div></div>
              <ul className="text-sm space-y-2">
                {summary.low_stock.map(m => (
                  <li key={m.id} className="flex items-center justify-between border-b border-[#E5E5E5] pb-2 last:border-0">
                    <span>{m.name} <span className="text-[#737373] text-xs">— {m.code || "no code"}</span></span>
                    <div className="flex items-center gap-3">
                      <span className="text-[#525252]">Libero {m.free} / min {m.min_stock} {m.unit}</span>
                      {canEdit && <button onClick={()=>openNew(m.id)} className="text-xs text-[#0A0A0A]">Restock</button>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card-dark">
            <div className="p-4 label-caps border-b border-[#E5E5E5]">Running stock</div>
            {materials.length === 0 ? <Empty title="No materials tracked"/> : (
              <table className="tbl">
                <thead><tr><th>Code</th><th>Name</th><th>Composition</th><th>Stock</th><th>Value</th><th></th></tr></thead>
                <tbody>
                  {materials.map(m => (
                    <tr key={m.id}>
                      <td className="text-[#0A0A0A]">{m.code || "—"}</td>
                      <td>{m.name}</td>
                      <td className="text-[#525252]">{m.composition || "—"}</td>
                      <td>{m.stock ?? 0} {m.unit}</td>
                      <td>€ {((m.stock||0)*(m.cost_per_unit||0)).toFixed(2)}</td>
                      <td>
                        {canEdit && <button onClick={()=>openNew(m.id)} className="text-xs text-[#0A0A0A]">+ Movement</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "ledger" && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="label-caps text-[#525252] inline-flex items-center gap-2"><History size={12}/> Movement history</div>
            <select className="input-dark py-1 text-xs" value={matFilter} onChange={(e)=>setMatFilter(e.target.value)} data-testid="inv-filter-material">
              <option value="">All materials</option>
              {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          {filteredMovements.length === 0 ? (
            <Empty
              title="No movements yet"
              hint={canEdit ? "Record your first movement" : ""}
              cta={canEdit ? { label: t("inventory.cta_first"), onClick: ()=>openNew() } : null}
              ctaTestId="inv-empty-cta"
            />
          ) : (
            <div className="card-dark overflow-x-auto" data-testid="inv-ledger">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th><th>Material</th><th>Reason</th>
                    <th className="text-right">Δ</th>
                    <th className="text-right">Before → After</th>
                    <th>Reference</th><th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMovements.map(m => {
                    const mat = matMap[m.material_id];
                    const positive = m.quantity_change > 0;
                    return (
                      <tr key={m.id} data-testid={`inv-row-${m.id}`}>
                        <td className="text-[#737373] text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</td>
                        <td>{mat?.name || m.material_id.slice(0,6)} <span className="text-[#737373] text-xs">{mat?.unit}</span></td>
                        <td><span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]">{m.reason.replaceAll("_"," ")}</span></td>
                        <td className={`text-right font-medium ${positive ? "text-emerald-400" : "text-red-600"}`}>
                          {positive ? <ArrowUpCircle size={12} className="inline mr-1"/> : <ArrowDownCircle size={12} className="inline mr-1"/>}
                          {positive ? "+" : ""}{m.quantity_change}
                        </td>
                        <td className="text-right text-xs text-[#525252]">{m.stock_before} → <span className="text-[#0A0A0A]">{m.stock_after}</span></td>
                        <td className="text-xs text-[#525252]">{m.reference_type ? `${m.reference_type}#${(m.reference_id||"").slice(0,6)}` : "—"}</td>
                        <td className="text-xs text-[#525252] max-w-[240px] truncate" title={m.notes}>{m.notes || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title="Record inventory movement">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label-caps block mb-2">Material</label>
            <select required className="input-dark" value={form.material_id} onChange={(e)=>setForm({...form, material_id: e.target.value})} data-testid="inv-form-material">
              <option value="">— select —</option>
              {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.stock ?? 0} {m.unit})</option>)}
            </select>
          </div>

          <div>
            <label className="label-caps block mb-2">Reason</label>
            <select className="input-dark" value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} data-testid="inv-form-reason">
              {REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label-caps block mb-2">Quantity</label>
            <input type="number" step="0.001" required className="input-dark" value={form.quantity_change} onChange={(e)=>setForm({...form, quantity_change: e.target.value})} data-testid="inv-form-qty"/>
            <div className="text-[10px] text-[#737373] mt-1">Sign auto-applied from reason (adjustment lets you enter negative)</div>
          </div>

          <div>
            <label className="label-caps block mb-2">Unit cost (optional)</label>
            <input type="number" step="0.01" className="input-dark" value={form.unit_cost} onChange={(e)=>setForm({...form, unit_cost: e.target.value})}/>
          </div>

          <div>
            <label className="label-caps block mb-2">Reference type</label>
            <select className="input-dark" value={form.reference_type} onChange={(e)=>setForm({...form, reference_type: e.target.value})}>
              <option value="">—</option>
              <option value="order">Order</option>
              <option value="garment">Garment</option>
              <option value="supplier">Supplier</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="label-caps block mb-2">Reference id / notes</label>
            <input className="input-dark mb-2" placeholder="Optional ref id" value={form.reference_id} onChange={(e)=>setForm({...form, reference_id: e.target.value})}/>
            <textarea className="input-dark" rows={2} placeholder="Notes" value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}/>
          </div>

          {err && <div className="text-red-600 text-sm md:col-span-2">{err}</div>}
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">Cancel</button>
            <button className="btn-gold" disabled={busy} data-testid="inv-form-save">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
